/**
 * Simple in-memory cache with TTL support
 * Used to reduce redundant API calls and improve performance
 */

const cache = new Map();

/**
 * Get cached data or fetch fresh data
 * @param {string} key - Cache key
 * @param {Function} fetchFn - Function to fetch data if not cached
 * @param {number} ttlMs - Time to live in milliseconds (default: 5 minutes)
 */
export const getCachedData = async (key, fetchFn, ttlMs = 5 * 60 * 1000) => {
  const cached = cache.get(key);
  
  if (cached && Date.now() < cached.expiresAt) {
    console.log(`📦 Cache hit: ${key}`);
    return cached.data;
  }
  
  console.log(`🔄 Cache miss: ${key}, fetching fresh data...`);
  const data = await fetchFn();
  
  cache.set(key, {
    data,
    expiresAt: Date.now() + ttlMs,
  });
  
  return data;
};

/**
 * Invalidate specific cache key
 */
export const invalidateCache = (key) => {
  if (key) {
    cache.delete(key);
    console.log(`🗑️ Cache invalidated: ${key}`);
  }
};

/**
 * Invalidate cache keys matching a pattern
 */
export const invalidateCachePattern = (pattern) => {
  const regex = new RegExp(pattern);
  let count = 0;
  
  for (const key of cache.keys()) {
    if (regex.test(key)) {
      cache.delete(key);
      count++;
    }
  }
  
  console.log(`🗑️ Invalidated ${count} cache entries matching: ${pattern}`);
};

/**
 * Clear all cache
 */
export const clearAllCache = () => {
  cache.clear();
  console.log('🗑️ All cache cleared');
};

/**
 * Get cache stats
 */
export const getCacheStats = () => {
  let validEntries = 0;
  let expiredEntries = 0;
  
  for (const [key, value] of cache.entries()) {
    if (Date.now() < value.expiresAt) {
      validEntries++;
    } else {
      expiredEntries++;
    }
  }
  
  return {
    totalEntries: cache.size,
    validEntries,
    expiredEntries,
  };
};

// Cache keys constants
export const CACHE_KEYS = {
  PHOTOGRAPHERS_LIST: 'photographers_list',
  PHOTOGRAPHER_PROFILE: (id) => `photographer_${id}`,
  PRICING_CONFIG: 'pricing_config',
  CITIES_LIST: 'cities_list',
  CATEGORIES_LIST: 'categories_list',
  USER_PROFILE: (id) => `user_profile_${id}`,
};

// Cache TTL constants (in milliseconds)
export const CACHE_TTL = {
  SHORT: 1 * 60 * 1000,      // 1 minute
  MEDIUM: 5 * 60 * 1000,     // 5 minutes
  LONG: 15 * 60 * 1000,      // 15 minutes
  VERY_LONG: 60 * 60 * 1000, // 1 hour
};

export default {
  getCachedData,
  invalidateCache,
  invalidateCachePattern,
  clearAllCache,
  getCacheStats,
  CACHE_KEYS,
  CACHE_TTL,
};
